"use client";

import React, { useState, useRef, useCallback } from "react";
import {
  CheckCircle2,
  XCircle,
  Circle,
  CircleDot,
  Sparkles,
  MessageSquare,
  Crown,
  History,
  HelpCircle,
} from "lucide-react";
import { ProblemHistory } from "@/lib/types";
import { shouldHideHistory } from "@/lib/anonymousSession";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { getForumUrl } from "@/lib/subject-utils";
import FeatureLockedModal from "@/components/FeatureLockedModal";
import PreviousSessionDialog from "@/components/PreviousSessionDialog";

type ProblemType = "기초" | "비추천" | "기출변형" | "기출";

const PROBLEM_TYPE_STYLES: Record<ProblemType, string> = {
  기초: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  비추천: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  기출변형: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  기출: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
};

const getPartialScoreText = (
  partialScore: NonNullable<ProblemHistory["attempts"][number]["partialScore"]>
) => {
  const percent =
    partialScore.totalItems > 0
      ? Math.round((partialScore.correctItems / partialScore.totalItems) * 100)
      : 0;
  return `${partialScore.correctItems}/${partialScore.totalItems} (${percent}%)`;
};

interface ProblemHistoryProps {
  isSignedIn?: boolean;
  user: { id: string } | null | undefined;
  id?: string;
  isLoadingHistory: boolean;
  problemHistory: ProblemHistory | null;
  isCorrectAnswerVisible: boolean;
  isCorrect?: boolean;
  showCurrentFeedback?: boolean;
  hasPremiumAccess?: boolean;
  problemTypes?: ProblemType[];
  premium?: boolean;
  variationNote?: string;
}

const ProblemHistoryComponent: React.FC<ProblemHistoryProps> = ({
  isSignedIn = false,
  user,
  id,
  isLoadingHistory,
  problemHistory,
  isCorrectAnswerVisible,
  isCorrect = false,
  showCurrentFeedback = true,
  hasPremiumAccess = false,
  problemTypes,
  premium = false,
  variationNote,
}) => {
  const router = useRouter();
  const hideHistory = !isSignedIn && shouldHideHistory();
  const [isLockModalOpen, setIsLockModalOpen] = useState(false);
  const [isStampVisible, setIsStampVisible] = useState(false);

  // 첫시도 도장: 뷰포트에 들어올 때 애니메이션 실행 (callback ref)
  const stampRef = useRef<HTMLSpanElement>(null);
  const stampObserverRef = useRef<IntersectionObserver | null>(null);

  const setStampRef = useCallback((el: HTMLSpanElement | null) => {
    // 이전 observer 정리
    if (stampObserverRef.current) {
      stampObserverRef.current.disconnect();
      stampObserverRef.current = null;
    }

    stampRef.current = el;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsStampVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );
    observer.observe(el);
    stampObserverRef.current = observer;
  }, []);

  // 로그인한 사용자이지만 프리미엄 접근 권한이 없는 경우
  const isLockedForNonPremium = isSignedIn && !hasPremiumAccess;

  // 풀이 이력 헬퍼 함수들
  const isFirstAttempt =
    isSignedIn &&
    user &&
    id &&
    (!problemHistory || problemHistory.attempts.length === 0);

  const getPreviousAttempts = () => {
    if (!problemHistory) return [];
    return problemHistory.attempts;
  };

  // 풀이이력을 숨겨야 하는지 여부 (비로그인 숨김 또는 프리미엄 잠금)
  const shouldHideHistorySlots = hideHistory || isLockedForNonPremium;

  // 5개의 슬롯을 만들고 오른쪽부터 채우는 함수
  const getFiveSlots = () => {
    // hideHistory 또는 프리미엄 잠금이면 모든 슬롯을 null로 반환 (회색 X로 표시)
    if (shouldHideHistorySlots) {
      return Array(5).fill(null);
    }

    const attempts = getPreviousAttempts();
    const recentAttempts = attempts.slice(-5); // 최근 5개만

    // 5개 슬롯 배열 생성 (null = 빈 슬롯)
    const slots: ((typeof recentAttempts)[0] | null)[] = Array(5).fill(null);

    // 오른쪽부터 채우기 (첫 번째 시도가 맨 오른쪽, 두 번째가 그 왼쪽)
    recentAttempts.forEach((attempt, index) => {
      const slotIndex = 4 - index; // 오른쪽 끝(4)부터 왼쪽으로
      slots[slotIndex] = attempt;
    });

    return slots;
  };

  const getOlderAttemptsStats = () => {
    if (shouldHideHistorySlots) return null;

    const attempts = getPreviousAttempts();
    if (attempts.length <= 5) return null;

    const olderAttempts = attempts.slice(0, -5);
    const correctCount = olderAttempts.filter((a) => a.isCorrect).length;
    const incorrectCount = olderAttempts.length - correctCount;

    return { correctCount, incorrectCount };
  };

  // 마지막 풀이 날짜 포맷팅 함수
  const formatLastAttemptDate = () => {
    if (
      !problemHistory ||
      !problemHistory.lastAttemptAt ||
      problemHistory.lastAttemptAt === 0
    ) {
      return null;
    }

    const date = new Date(problemHistory.lastAttemptAt);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}.${month}.${day}`;
  };

  // Attempt timestamp 포맷팅 함수
  const formatAttemptTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePremiumUpgrade = () => {
    router.push("/membership");
  };

  // 문제 ID가 없거나 로딩 중이면 표시하지 않음
  if (!id || isLoadingHistory) {
    return null;
  }

  // 로그인 유저인데 user가 없으면 표시하지 않음
  if (isSignedIn && !user) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* 왼쪽: 문제 유형 배지 + 현재 상태 (항상 렌더링하여 구조 유지) */}
        <div className="flex items-center gap-2">
          {(() => {
            const hasExam = problemTypes?.includes("기출") ?? false;
            const headerTypes = problemTypes?.filter((t) => !(hasExam && t === "기출변형")) ?? [];
            return headerTypes.length > 0 && (
            <div className="relative flex items-center gap-1">
              {headerTypes.map((type) => {
                const showVariationTooltip = type === "기출변형" && Boolean(variationNote);
                return (
                  <span
                    key={type}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-sm font-semibold ${PROBLEM_TYPE_STYLES[type]}`}
                  >
                    {type}
                    {showVariationTooltip && (
                      <Tooltip tapToOpen>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="기출변형 사유 보기"
                            className="inline-flex min-h-7 min-w-7 items-center justify-center rounded-full text-purple-600 transition-colors hover:bg-purple-100 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-400 dark:text-purple-300 dark:hover:bg-purple-950/50 dark:hover:text-purple-100"
                          >
                            <HelpCircle className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-sm max-w-80 md:!max-w-2xl !w-auto !text-left !text-wrap">
                          <div className="text-left whitespace-pre-line leading-relaxed">
                            {variationNote}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                );
              })}
              {!shouldHideHistorySlots && isFirstAttempt && !isCorrectAnswerVisible && (
                <span
                  ref={setStampRef}
                  className={`absolute -top-5 -right-7 text-base font-bold text-red-600 dark:text-red-400 ${isStampVisible ? "animate-stamp" : "opacity-0"}`}
                >
                  첫시도!
                </span>
              )}
            </div>
            );
          })()}
          {(!problemTypes || problemTypes.length === 0) && !shouldHideHistorySlots && isFirstAttempt && !isCorrectAnswerVisible && (
            <span className="text-base font-bold text-red-600 dark:text-red-400">
              첫 시도!
            </span>
          )}
          {isCorrectAnswerVisible && showCurrentFeedback && (
            <>
              {isCorrect ? (
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              )}
            </>
          )}
        </div>

        {/* 중간: 프리미엄 아이콘, 토론 버튼 및 마지막 풀이 날짜 */}
        <div className="flex items-center gap-2 flex-wrap">
          {premium && (
            id?.includes("_final_") ? (
              <span className="inline-flex items-center gap-1 text-violet-600 dark:text-violet-400">
                <Crown className="h-3.5 w-3.5" />
                <span className="hidden md:inline text-xs font-medium">프리미엄 파이널</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                <Crown className="h-3.5 w-3.5" />
                <span className="hidden md:inline text-xs font-medium">프리미엄</span>
              </span>
            )
          )}
          {getForumUrl(id!) && (
            <Button variant="outline" size="sm" asChild>
              <Link href={getForumUrl(id!)!} target="_blank">
                <MessageSquare className="h-3 w-3" />
                토론
              </Link>
            </Button>
          )}
          {/* 마지막 시도 날짜 */}
          {!shouldHideHistorySlots && formatLastAttemptDate() && (
            <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              마지막 시도: {formatLastAttemptDate()}
            </span>
          )}
        </div>

        {/* 오른쪽: 이전 풀이 이력 (5개 슬롯 항상 표시) */}
        <div className="flex items-center gap-2">
          {/* 업글 버튼: 숨김 상태일 때 표시 */}
          {shouldHideHistorySlots && (
            <Button
              onClick={handlePremiumUpgrade}
              size="sm"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:from-yellow-500 hover:to-orange-600 dark:from-yellow-500 dark:to-orange-600 dark:hover:from-yellow-600 dark:hover:to-orange-700"
            >
              <Sparkles className="h-3 w-3 mr-1" />
              업글
            </Button>
          )}
          {/* 이전 회차 풀이 기록 (5개 슬롯 바로 왼쪽, 아이콘 버튼) */}
          {!shouldHideHistorySlots && isSignedIn && user && id && (
            <PreviousSessionDialog
              mode="problemHistory"
              userId={user.id}
              problemId={id}
              trigger={
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="이전 회차 풀이 기록"
                  title="이전 회차 풀이 기록"
                >
                  <History className="h-3.5 w-3.5" />
                </Button>
              }
            />
          )}
          {/* 5개 슬롯 (왼쪽부터 오른쪽으로, 오른쪽이 최신) */}
          <div 
            className={`flex items-center gap-1 ${shouldHideHistorySlots ? "cursor-pointer" : ""}`}
            onClick={shouldHideHistorySlots ? () => setIsLockModalOpen(true) : undefined}
          >
            {getFiveSlots().map((attempt, index) => {
              const partialScore =
                attempt && !attempt.isCorrect && attempt.partialScore?.correctItems
                  ? attempt.partialScore
                  : null;
              const iconElement = shouldHideHistorySlots ? (
                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : attempt === null ? (
                <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600" />
              ) : attempt.isCorrect ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : partialScore ? (
                <CircleDot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              );

              // attempt가 있는 경우에만 tooltip 표시
              if (attempt && !shouldHideHistorySlots) {
                return (
                  <Tooltip tapToOpen key={index}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="flex min-h-6 min-w-6 cursor-pointer items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                        aria-label={
                          partialScore
                            ? `부분 점수: ${getPartialScoreText(partialScore)}, 풀이 시간: ${formatAttemptTimestamp(attempt.timestamp)}`
                            : `풀이 시간: ${formatAttemptTimestamp(attempt.timestamp)}`
                        }
                      >
                        {iconElement}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {partialScore ? (
                        <>
                          부분 점수 {getPartialScoreText(partialScore)}
                          <br />
                          {formatAttemptTimestamp(attempt.timestamp)}
                        </>
                      ) : (
                        formatAttemptTimestamp(attempt.timestamp)
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <div key={index} className="flex items-center">
                  {iconElement}
                </div>
              );
            })}
          </div>

          {/* 6번 이상 풀었을 때 오래된 시도 통계 */}
          {getOlderAttemptsStats() && (
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span>+</span>
              <span>
                <CheckCircle2 className="inline h-3 w-3 text-green-600 dark:text-green-400" />
                : {getOlderAttemptsStats()!.correctCount}
              </span>
              <span>
                <XCircle className="inline h-3 w-3 text-red-600 dark:text-red-400" />
                : {getOlderAttemptsStats()!.incorrectCount}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 프리미엄 기능 잠금 모달 */}
      <FeatureLockedModal
        isOpen={isLockModalOpen}
        featureType="problemHistory"
        onClose={() => setIsLockModalOpen(false)}
      />
    </div>
  );
};

export default ProblemHistoryComponent;
